/**
 * ConsentBanner — GDPR-compliant cookie consent banner
 *
 * Layer 1 of the two-layer consent UI (CookieYes/OneTrust pattern).
 * Renders a fixed-bottom banner on first visit (when consent is null).
 * Three equal-weight buttons: Reject All, Manage Preferences, Accept All.
 * No dark patterns — all buttons have equal visual prominence.
 *
 * @epic MVP 0.1.1.2 — Cookie Consent & Preference Layer
 * @story SCRUM-1446
 */

import React from 'react';
import useConsentPreference from '../../hooks/useConsentPreference';

const ConsentBanner = () => {
  const { consent, acceptAll, rejectAll, openPreferences } = useConsentPreference();

  // Only render when consent is null (first visit, no odg_required cookie)
  if (consent !== null) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
      className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t border-gray-700 shadow-lg"
    >
      <div className="max-w-4xl mx-auto px-4 py-4 sm:py-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-gray-200 leading-snug">
            We use cookies to improve your experience.{' '}
            <a
              href="/privacy"
              className="text-emerald-400 hover:text-emerald-300 underline"
            >
              Privacy Policy
            </a>
          </p>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={rejectAll}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Reject All
            </button>
            <button
              type="button"
              onClick={openPreferences}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Manage Preferences
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
